/**
 * Base class for shader extensions that allow external code to customize
 * the rendering pipeline without modifying core renderer shaders.
 *
 * Subclasses override hook methods to provide WGSL chunks that get compiled
 * into the shader pipeline, and can declare additional GPU bindings with
 * per-batch upload logic.
 *
 * Note: ShaderExtensions are...
 *  - only supported in WebGPU
 *  - not intended for public sharing soon - only to allow us to properly decouple extension-specific WebGPU shader code.
 *    Sharing it too widely soon would open up a flexibility would make maintenance harder and restrict us wrt. to late renderer changes.
 **/
export class ShaderExtension {

  /** @type {GPUDevice|null} Set by the Renderer on registration. */
  _device = null;

  /**
   * Callback set by the Renderer. The extension must call this whenever its
   * GPU buffer is reallocated, so that bind groups can be recreated.
   * @type {function|null}
   */
  onBindGroupInvalidated = null;

  /**
   * Set by the Renderer before init(). Returns the next available binding
   * index for the given bind group number.
   * @type {function(number): number|null}
   */
  _acquireBinding = null;

  // --- Shader code hooks ---

  /**
   * Can be used by ShaderExtensions to override the model matrix of each mesh in all shaders.
   *
   * Scope: Applied in all 3D render passes (uber, basic, edge, ground shadow depth).
   * Not applied in 2D shaders (leaflet, line-screen-space, sheet), GPU culling, or
   * third-party shader extensions (e.g. prism).
   *
   * Limitations:
   *  - Only affects WebGPU rendering (not WebGL).
   *  - Changes are only visible on GPU — CPU-side bounding boxes and hit-testing are
   *    unaware of the override. (TODO: We need a general concept to consistently address culling too)
   *  - GPU-driven culling (experimental) does not apply the hook yet. Its compute
   *    shader has its own bind group layout (group 1, bindings 1-5), so extension
   *    hooks that declare extra bindings would conflict. Supporting it requires
   *    either a separate bind group for extension data or a binding-free hook variant.
   *    TODO: integrate once GPU culling bind group layout is refactored.
   *
   * Returns a WGSL chunk that defines `getEffectiveModelMatrix(u: ObjectUniforms, instance: u32) -> mat4x4f`.
   * The chunk may include any helper functions and uniform declarations it needs.
   * Return null to indicate no override (default behavior will be used).
   *
   * Example:
   * ```wgsl
   *
   * fn getEffectiveModelMatrix(u: ObjectUniforms, instance: u32) -> mat4x4f {
   *     var m = getModelMatrix(u);
   *     m[3].y += 10.0; // shift all meshes up by 10 units
   *     return m;
   * }
   *
   * ```
   *
   * @returns {string|null}
   */
  getModelMatrixHookChunk() {
    return null;
  }

  // --- Bind group integration ---
  //
  // Extensions can declare additional GPU bindings in group 1 (per-batch).
  // Binding numbers are auto-assigned by calling acquireBinding() during init(),
  // which returns the next free index. Extensions must use these assigned numbers
  // in their getBindGroupLayoutEntries(), getBindGroupEntries(), and WGSL chunks.

  /**
   * Acquires the next available binding index for the given bind group.
   * Call during init() to get a unique binding number for each buffer your
   * extension needs. Use the returned number in getBindGroupLayoutEntries(),
   * getBindGroupEntries(), and WGSL binding declarations.
   *
   * Example:
   * ```js
   * init(device) {
   *     super.init(device);
   *     this._binding = this.acquireBinding(1);
   * }
   * getModelMatrixHookChunk() {
   *     return `
   *         @group(1) @binding(${this._binding}) var<storage> myBuffer : array<f32>;
   *         fn getEffectiveModelMatrix(u: ObjectUniforms, instance: u32) -> mat4x4f { ... }
   *     `;
   * }
   * getBindGroupLayoutEntries() {
   *     return [{ binding: this._binding, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } }];
   * }
   * ```
   *
   * @param {number} group - The bind group number (currently only 1 is supported).
   * @returns {number} The assigned binding index.
   */
  acquireBinding(group) {
    if (!this._acquireBinding) {
      throw new Error('acquireBinding() called outside of init() — the renderer has not set up the allocator yet');
    }
    return this._acquireBinding(group);
  }

  /**
   * Returns additional bind group layout entries for group 1 (per-batch group).
   * Each entry describes a slot's type and shader stage visibility.
   * Use binding numbers obtained from acquireBinding().
   *
   * Example:
   * ```js
   *
   * getBindGroupLayoutEntries() {
   *     return [{
   *         binding: this._binding, // from acquireBinding(1)
   *         visibility: GPUShaderStage.VERTEX,
   *         buffer: { type: 'read-only-storage' }
   *     }];
   * }
   *
   * ```
   *
   * @returns {GPUBindGroupLayoutEntry[]}
   */
  getBindGroupLayoutEntries() {
    return [];
  }

  /**
   * Returns additional bind group entries for group 1 (per-batch group).
   * Each entry provides the actual GPU buffer for a slot declared in the layout.
   * Use binding numbers obtained from acquireBinding().
   *
   * Example:
   * ```js
   *
   * getBindGroupEntries() {
   *     return [{
   *         binding: this._binding, // from acquireBinding(1)
   *         resource: { buffer: this._myStorageBuffer }
   *     }];
   * }
   *
   * ```
   *
   * @returns {GPUBindGroupEntry[]}
   */
  getBindGroupEntries() {
    return [];
  }

  // --- Per-batch upload ---
  //
  // Some extensions need to update GPU data before each batch is drawn, e.g.
  // to write per-batch animation parameters into a shared buffer. When enabled,
  // the renderer serializes batch submission (one submit per batch) so that each
  // writeBuffer is consumed before the next batch overwrites it.

  /**
   * Returns true if the extension currently needs per-batch GPU uploads.
   * When true, the renderer calls uploadForBatch() before each batch draw.
   * When false, the extension is skipped entirely (zero overhead).
   *
   * Typically toggled dynamically: return true only while an animation is
   * running, false otherwise.
   * @returns {boolean}
   */
  needsPerBatchUpload() {
    return false;
  }

  /**
   * Uploads extension-specific data for the given RenderBatch.
   * Called before drawing each batch when needsPerBatchUpload() is true.
   *
   * Example: write per-instance animation transforms for the fragments in this batch.
   *
   * @param {*} renderBatch - The RenderBatch about to be drawn.
   * @param {number} instanceOffset - GPU uniform index where this batch starts,
   *     useful for indexing into per-instance buffers.
   */
  uploadForBatch(renderBatch, instanceOffset) {

    // no-op by default
  }
  // --- Lifecycle ---

  /**
   * Called by the Renderer when the extension is registered.
   * Subclasses can override to initialize GPU resources. Use acquireBinding()
   * here to reserve binding indices before returning.
   * @param {GPUDevice} device
   */
  init(device) {
    this._device = device;
  }

  /**
   * Called by the Renderer when the extension is removed.
   * Subclasses should override to destroy GPU resources.
   */
  dispose() {
    this._device = null;
    this.onBindGroupInvalidated = null;
    this._acquireBinding = null;
  }
}